import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import {
  FiSearch, FiFilter, FiStar, FiShoppingCart, FiHeart,
  FiCheck, FiChevronDown, FiPackage, FiTruck, FiShield, FiRefreshCw,
} from 'react-icons/fi';
import { GiChiliPepper, GiHerbsBundle, GiCoolSpices, GiWheat } from 'react-icons/gi';
import { MdLocalFlorist } from 'react-icons/md';
import './ShopPage.css';

export const iconMap = {
  cool:    <GiCoolSpices />,
  chili:   <GiChiliPepper />,
  herb:    <GiHerbsBundle />,
  wheat:   <GiWheat />,
  florist: <MdLocalFlorist />,
};

const categories = ['All', 'Whole Spices', 'Powders', 'Blends & Masala', 'Sauces', 'Honey & Health'];
const sortOptions = ['Featured', 'Price: Low to High', 'Price: High to Low', 'Best Sellers'];

const CATEGORY_ORDER = ['Whole Spices', 'Powders', 'Blends & Masala', 'Sauces', 'Honey & Health'];
const CATEGORY_META = {
  'Whole Spices':    { emoji: '🌱', desc: 'Unground, full-flavour seeds & pods' },
  'Powders':         { emoji: '✨', desc: 'Stone-ground for maximum freshness' },
  'Blends & Masala': { emoji: '🍛', desc: 'Signature East African spice blends' },
  'Sauces':          { emoji: '🌶️', desc: 'Small-batch chilli & flavour sauces' },
  'Honey & Health':  { emoji: '🍯', desc: 'Raw honey & wellness products' },
};

const benefits = [
  { icon: <FiTruck />,    title: 'Fast Delivery',      text: 'Dar es Salaam same day' },
  { icon: <FiShield />,   title: 'Quality Guarantee',  text: 'Lab-tested, farm-fresh' },
  { icon: <FiRefreshCw />,title: 'Easy Returns',       text: 'Hassle-free policy' },
  { icon: <FiPackage />,  title: 'Fresh Packed',       text: 'Sealed within 21 days' },
];

const ProductCard = ({ product, wishlist, onWishlistToggle }) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  const handleAddToCart = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="shop-product-card">
      {product.badge && (
        <span className={`product-badge badge-${product.badge.toLowerCase().replace(/[^a-z]/g, '-')}`}>
          {product.badge}
        </span>
      )}
      <button className={`wishlist-btn ${wishlist ? 'active' : ''}`} onClick={onWishlistToggle} aria-label="Wishlist">
        <FiHeart />
      </button>

      <div className="product-icon-area">
        {product.imageSrc ? (
          <img src={product.imageSrc} alt={product.name} className="product-img"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        ) : null}
        <div className="product-icon" style={{ display: product.imageSrc ? 'none' : 'flex' }}>
          {iconMap[product.iconType] || iconMap.cool}
        </div>
        {discount && <span className="discount-pill">-{discount}%</span>}
      </div>

      <div className="product-info">
        <span className="product-origin">{product.origin}</span>
        <h3 className="product-name">{product.name}</h3>
        <p className="product-desc">{product.description}</p>
        <div className="product-meta">
          <div className="product-rating">
            <FiStar fill="#fabc2a" color="#fabc2a" size={13} />
            <span className="rating-value">{product.rating}</span>
            <span className="rating-count">({product.reviews})</span>
          </div>
          <span className="product-weight">{product.weight}</span>
        </div>
        <div className="product-pricing">
          <span className="price-current">TZS {Number(product.price).toLocaleString()}</span>
          {product.originalPrice && (
            <span className="price-original">TZS {Number(product.originalPrice).toLocaleString()}</span>
          )}
        </div>
        <button className={`add-to-cart-btn ${added ? 'added' : ''}`} onClick={handleAddToCart}>
          {added ? <FiCheck /> : <FiShoppingCart />}
          <span>{added ? 'Added!' : 'Add to Cart'}</span>
        </button>
      </div>
    </div>
  );
};

const ShopPage = () => {
  const { products } = useProducts();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(
    () => location.state?.category || 'All'
  );
  const [activeSort, setActiveSort] = useState('Featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [wishlist, setWishlist] = useState({});

  // Re-apply category if navigating here from another page (e.g. footer link)
  useEffect(() => {
    if (location.state?.category) {
      setActiveCategory(location.state.category);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [sortOpen, setSortOpen] = useState(false);

  const toggleWishlist = (id) => setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));

  const filtered = products
    .filter((p) => activeCategory === 'All' || p.category === activeCategory)
    .filter((p) => !searchQuery ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (activeSort === 'Price: Low to High') return a.price - b.price;
      if (activeSort === 'Price: High to Low') return b.price - a.price;
      if (activeSort === 'Best Sellers') return b.reviews - a.reviews;
      return 0;
    });

  return (
    <div className="shop-page">
      <section className="shop-hero">
        <div className="shop-hero-content">
          <div className="shop-hero-eyebrow">
            <span className="eyebrow-line" />
            <span>Authentic Tanzanian Spices</span>
            <span className="eyebrow-line" />
          </div>
          <h1 className="shop-hero-title">Our Spice <span className="shop-title-accent">Collection</span></h1>
          <p className="shop-hero-subtitle">{products.length} products sourced directly from farms across Tanzania.</p>
          <div className="shop-search-bar">
            <FiSearch className="search-icon-shop" />
            <input type="text" placeholder="Search spices, blends, honey..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="shop-search-input" />
          </div>
        </div>
      </section>

      <div className="shop-benefits-strip">
        {benefits.map((b, i) => (
          <div key={i} className="benefit-item">
            <span className="benefit-icon">{b.icon}</span>
            <div><strong>{b.title}</strong><span>{b.text}</span></div>
          </div>
        ))}
      </div>

      <section className="shop-main">
        <div className="shop-controls">
          <div className="category-tabs">
            {categories.map((cat) => (
              <button key={cat} className={`category-tab ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}>
                {activeCategory === cat && <FiCheck size={12} />}{cat}
              </button>
            ))}
          </div>
          <div className="shop-sort-row">
            <span className="results-count">{filtered.length} products</span>
            <div className="sort-dropdown">
              <button className="sort-btn" onClick={() => setSortOpen(!sortOpen)} aria-expanded={sortOpen}>
                <FiFilter size={14} /><span>{activeSort}</span>
                <FiChevronDown size={14} style={{ transform: sortOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
              </button>
              {sortOpen && (
                <div className="sort-options">
                  {sortOptions.map((opt) => (
                    <button key={opt} className={`sort-option ${activeSort === opt ? 'active' : ''}`}
                      onClick={() => { setActiveSort(opt); setSortOpen(false); }}>
                      {activeSort === opt && <FiCheck size={12} />}{opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {filtered.length > 0 ? (
          activeCategory === 'All' && !searchQuery ? (
            /* ── Grouped by category ── */
            CATEGORY_ORDER.map((cat) => {
              const catProducts = filtered.filter((p) => p.category === cat);
              if (catProducts.length === 0) return null;
              const meta = CATEGORY_META[cat] || {};
              return (
                <div key={cat} className="shop-category-section">
                  <div className="shop-cat-header">
                    <span className="cat-emoji">{meta.emoji}</span>
                    <div className="cat-header-text">
                      <h2 className="cat-title">{cat}</h2>
                      <p className="cat-desc">{meta.desc}</p>
                    </div>
                    <span className="cat-count">{catProducts.length} items</span>
                  </div>
                  <div className="shop-product-grid">
                    {catProducts.map((product) => (
                      <ProductCard key={product.id} product={product}
                        wishlist={wishlist[product.id]}
                        onWishlistToggle={() => toggleWishlist(product.id)} />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            /* ── Flat grid for filtered / search ── */
            <div className="shop-product-grid">
              {filtered.map((product) => (
                <ProductCard key={product.id} product={product}
                  wishlist={wishlist[product.id]}
                  onWishlistToggle={() => toggleWishlist(product.id)} />
              ))}
            </div>
          )
        ) : (
          <div className="no-results">
            <GiCoolSpices size={48} />
            <h3>No spices found</h3>
            <p>Try a different search term or category.</p>
            <button onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}>Clear filters</button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ShopPage;
